import { create } from 'zustand';
import i18n from '../i18n';
import type { LanguageCode } from '../types/i18n';
import { detectInitialLanguage, persistLanguage } from '../utils/languageDetect';

interface LanguageState {
  currentLanguage: LanguageCode;
  supportedLanguages: LanguageCode[];
  defaultLanguage: LanguageCode;
  isInitialized: boolean;

  setLanguage: (lang: LanguageCode) => void;
  initializeFromTenant: (
    supportedLanguages: LanguageCode[],
    defaultLanguage: LanguageCode,
    preferredLanguage?: LanguageCode | null,
  ) => void;
  resetToDefault: () => void;
}

const VALID_CODES: LanguageCode[] = ['uz', 'ru', 'en'];

export const useLanguageStore = create<LanguageState>((set, get) => ({
  currentLanguage: 'uz',
  supportedLanguages: ['uz', 'ru', 'en'],
  defaultLanguage: 'uz',
  isInitialized: false,

  setLanguage: (lang) => {
    persistLanguage(lang);
    void i18n.changeLanguage(lang);
    set({ currentLanguage: lang });
  },

  initializeFromTenant: (supportedLanguages, defaultLanguage, preferredLanguage) => {
    if (get().isInitialized) return;

    const filteredSupported = supportedLanguages.filter((l): l is LanguageCode =>
      VALID_CODES.includes(l as LanguageCode),
    );
    const validDefault: LanguageCode = VALID_CODES.includes(defaultLanguage as LanguageCode)
      ? (defaultLanguage as LanguageCode)
      : filteredSupported[0] ?? 'uz';

    const currentLanguage = detectInitialLanguage(
      filteredSupported,
      validDefault,
      preferredLanguage,
    );
    persistLanguage(currentLanguage);
    void i18n.changeLanguage(currentLanguage);

    set({
      currentLanguage,
      supportedLanguages: filteredSupported.length > 0 ? filteredSupported : VALID_CODES,
      defaultLanguage: validDefault,
      isInitialized: true,
    });
  },

  resetToDefault: () => {
    const { defaultLanguage } = get();
    persistLanguage(defaultLanguage);
    void i18n.changeLanguage(defaultLanguage);
    set({ currentLanguage: defaultLanguage });
  },
}));
